import { useCallback, useState } from 'react';

/*
 SC-QA-119: a report past the translation cut-off comes back with `translation { lang, status, untranslated }`, from
 each part of the page (our inspection, the service history, the AI overview). The page says when a translation was
 not possible (decision 17: the text is in English, or as written, with a Retry button) or when some fields stayed in
 English. Reports from before the cut-off carry no `translation`, and the page says nothing.
*/

// The state of the whole page: unavailable if any part is, partial if any part is, else null (nothing to say)
export const translationStatus = (...translations) => {
    const statuses = translations.filter(Boolean).map(translation => translation.status);
    if (statuses.includes('unavailable')) return 'unavailable';
    if (statuses.includes('partial')) return 'partial';
    return null;
};

// The notice for that state, with a Retry action that asks again (each saved language is kept, so a retry only
// costs a translation for what is still missing)
export const translationNotice = (status, t, onRetry) => {
    if (!status) return null;
    const unavailable = status === 'unavailable';
    return {
        tone: unavailable ? 'warn' : 'info',
        title: t(unavailable ? 'translation.unavailableTitle' : 'translation.partialTitle'),
        text: t(unavailable ? 'translation.unavailableText' : 'translation.partialText'),
        screenOnlyText: true,
        action: onRetry ? { label: t('translation.retry'), onClick: onRetry } : null,
    };
};

// A counter that requests depend on: retry() bumps it, so they run again
export const useRetry = () => {
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => setAttempt(n => n + 1), []);
    return [attempt, retry];
};

// The notices a page shows, with the translation notice first
export const withTranslationNotice = (report, notice) =>
    report && notice ? { ...report, notices: [notice, ...(report.notices || [])] } : report;
